import React from "react";

import applovinLogo from "@assets/applovin.svg";
import PartnerConsentSwitch from "@features/partner-consent/shared/partner-consent-switch";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { useFeatureFlags } from "@hooks/http";
import {
  ConnectionType,
  Partner,
  PartnerPermissionEnum,
} from "@north-beam/nb-client-side";
import {
  ConnectionCard,
  type PartnerConsentComponentProps,
} from "@pages/settings/connection-card";
import {
  type LoginComponentChildrenProps,
  type LoginComponentProps,
} from "@pages/settings/connection-status-manager";
import { PrimaryButton } from "@shared/buttons";

const type = ConnectionType.integration_applovin;

const PartnerConsent = ({
  hideWhenMissingConsent,
}: PartnerConsentComponentProps) => (
  <PartnerConsentSwitch
    partner={Partner.APPLOVIN}
    permissions={[PartnerPermissionEnum.data_impression_ingestion]}
    label="Turn on to use Clicks + Deterministic Views Model"
    hideWhenMissingConsent={hideWhenMissingConsent}
  />
);

export function ApplovinConnectionCard() {
  const { hasFeatureFlag } = useFeatureFlags();

  const isMultipleAccountsEnabled = hasFeatureFlag(
    "enableMultipleAppLovinAccounts",
  );
  return (
    <ConnectionCard
      type={type}
      connectionTypeName="AppLovin"
      connectionCallToAction={`
        Integrate your AppLovin account
      `}
      logoUrl={applovinLogo}
      logoAltText="AppLovin Logo"
      partnerConsentComponent={PartnerConsent}
      supportMultipleAccounts={
        isMultipleAccountsEnabled ? "primaryAccounts" : undefined
      }
      successfulConnectionExtraText={() => null}
    >
      {({
        upsertConnection,
        isLoading,
        accounts,
      }: LoginComponentChildrenProps) => (
        <AppLovinLoginComponent
          upsertConnection={upsertConnection}
          isLoading={isLoading}
          accounts={accounts}
        />
      )}
    </ConnectionCard>
  );
}

function AppLovinLoginComponent({
  upsertConnection,
  isLoading,
}: Omit<LoginComponentProps, "setStatus" | "integrationDetails">) {
  const [apiReportingKey, setApiReportingKey] = React.useState("");
  const [accountId, setAccountId] = React.useState("");

  const onConnectClicked = React.useCallback(async () => {
    await upsertConnection({
      apiReportingKey: apiReportingKey.trim(),
      account_id: accountId.trim(),
    });
  }, [accountId, apiReportingKey, upsertConnection]);

  return (
    <div className="mb-3">
      <div className="flex flex-col gap-y-5 mt-5">
        <div>
          <div>
            Account ID
            <LearnMoreTooltip
              title="AppLovin Account ID"
              content="You should provide your AppLovin Account ID to connect your account."
              link="https://docs.northbeam.io/docs/applovin#step-2-find-your-account-id"
            />
          </div>
          <input
            disabled={isLoading}
            type="text"
            className="form-control max-w-xl"
            value={accountId}
            placeholder="Account ID..."
            id="accountId"
            onChange={(event) => setAccountId(event.target.value)}
          />
        </div>
        <div>
          <div>
            API Reporting Key
            <LearnMoreTooltip
              title="AppLovin Reporting Key"
              content="Provide your Reporting Key to allow Northbeam to pull data from your AppLovin account."
              link="https://docs.northbeam.io/docs/applovin#step-1-report-api-key"
            />
          </div>
          <input
            disabled={isLoading}
            type="text"
            className="form-control max-w-xl"
            value={apiReportingKey}
            placeholder="API Reporting Key..."
            id="applovinApiReportingKey"
            onChange={(event) => setApiReportingKey(event.target.value)}
          />
        </div>
        <div>
          <PrimaryButton
            loading={isLoading}
            disabled={!apiReportingKey.trim() || !accountId.trim()}
            onClick={onConnectClicked}
          >
            {isLoading ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
